import { useState } from 'react'
import { Button, Input, InputNumber, message, Select, Space, Typography } from 'antd'
import { jsonToolbox } from '@/api/payloadDebug'
import type { JsonAction } from '@/types'

const { Text } = Typography

/** 动作选项 */
const ACTION_OPTIONS: { value: JsonAction; label: string }[] = [
  { value: 'format', label: '格式化' },
  { value: 'validate', label: '校验' },
  { value: 'jsonpath', label: 'JSONPath 提取' },
  { value: 'schema', label: '生成 Schema' },
]

const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

/** JSON 工具箱页签：格式化/校验/JSONPath 提取/Schema 生成 */
export default function JsonToolboxTab() {
  const [action, setAction] = useState<JsonAction>('format')
  const [indent, setIndent] = useState(2)
  const [jsonpath, setJsonpath] = useState('$.store.book[0].title')
  const [content, setContent] = useState('')
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)

  /** 执行动作（校验结果显示通过/失败明细） */
  const handleRun = async () => {
    if (!content.trim()) {
      message.warning('请输入 JSON 文本')
      return
    }
    setLoading(true)
    try {
      const res = await jsonToolbox({ action, content, indent, jsonpath: jsonpath || undefined })
      if (action === 'validate') {
        setResult(res.message || 'JSON 格式合法')
        message.success('JSON 格式合法')
        return
      }
      if (action === 'format') {
        setResult(res.formatted ?? '')
      } else if (action === 'jsonpath') {
        setResult(
          res.count
            ? (res.values ?? []).map((v) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2))).join('\n')
            : '',
        )
        message.success(`提取到 ${res.count} 个值`)
      } else {
        setResult(JSON.stringify(res.schema, null, 2))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(result)
    message.success('已复制结果')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px' }}>
      {/* 工具栏 */}
      <Space wrap style={{ flexShrink: 0 }}>
        <Select value={action} onChange={setAction} style={{ width: 150 }}
          options={ACTION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
        {action === 'format' && (
          <InputNumber min={0} max={8} value={indent} onChange={(v) => setIndent(v || 2)}
            addonBefore="缩进" style={{ width: 130 }} />
        )}
        {action === 'jsonpath' && (
          <Input
            placeholder="$.store.book[*].author"
            value={jsonpath}
            onChange={(e) => setJsonpath(e.target.value)}
            style={{ width: 320, ...MONO }}
          />
        )}
        <div style={{ flex: 1 }} />
        <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }}
          loading={loading} onClick={handleRun}>
          执行
        </Button>
      </Space>

      {/* 左输入右输出 */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Text strong style={{ marginBottom: 8 }}>输入 JSON</Text>
          <Input.TextArea
            placeholder='粘贴 JSON 报文，如 {"code": 0, "data": {...}}'
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={{ ...MONO, flex: 1, minHeight: 0, resize: 'none' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Space style={{ marginBottom: 8 }}>
            <Text strong>{action === 'validate' ? '校验结果' : '结果'}</Text>
            <div style={{ flex: 1 }} />
            <Button size="small" disabled={!result} onClick={handleCopy}>复制</Button>
          </Space>
          <Input.TextArea
            readOnly
            value={result}
            placeholder="结果将显示在这里…"
            style={{ ...MONO, flex: 1, minHeight: 0, background: 'var(--au-bg-cream)', resize: 'none' }}
          />
        </div>
      </div>
    </div>
  )
}
